import { isMyDayProposal } from '../../data/sol/sol-contract';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useSession } from '../auth/session';
import { useSolAsk } from './use-sol-ask';
import { useSolReadAloud } from './use-sol-read-aloud';
import { SolAnswerView } from './SolAnswerView';
import { SolReadAloud } from './SolReadAloud';
import { SolActionProposalCard } from './SolActionProposalCard';
import { SolMyDayProposalCard } from './SolMyDayProposalCard';
import { SolMemoryClearCard } from './SolMemoryClearCard';
import { SolMemoryNotedCard } from './SolMemoryNotedCard';
import { presentRestoredSolMessage } from './present-sol-result';
import { solHistoryPath, SOL_MEMORY_PATH } from './sol-routes';
import { useDictation } from '../dictation/use-dictation';
import { deliverTranscript } from '../dictation/dictation-text';
import {
  ASK_UNAVAILABLE_TEXT,
  SIGNED_OUT_TEXT,
  SOL_EMPTY_PROMPT,
  SOL_STANDING_WARNING,
} from './sol-wording';
import './sol.css';

/**
 * ASK SOL — the reusable surface. M9 / S09-04.
 *
 * **IT TAKES PROJECT CONTEXT AS PROPS AND NEVER READS THE ROUTE** (D-04-2).
 * `useParams()` appears exactly once in this feature, in the thin
 * `AskSolScreen` wrapper. Sol is not a Project-only product: a future global
 * `/sol` mounts this same component with `currentProjectId = null`, and the only
 * thing that has to change is the wrapper. A `useParams()` in here would make
 * that a rewrite instead of a route.
 *
 * **NO SCOPE CONTROL** (D-04-1). The design export drew a `Searching: … ▾` chip;
 * the endpoint has no field that would honour it, so drawing one would be a
 * control that looks like it changes where Crewlink searches and does not.
 * What is shown instead is the Project he is already in, and — AFTER a result —
 * the server's own deterministic disclosure of what was actually searched.
 *
 * **NO CONVERSATIONAL MEMORY, AND THE LAYOUT SAYS SO** (D-04-3). Earlier
 * questions stay visible under a plain heading, deliberately NOT as chat
 * bubbles: bubbles promise that "what about that one?" will be understood, and
 * it will not be. Every request is independent, nothing is persisted, and
 * leaving this screen ends the list.
 */

export interface SolConversationProps {
  /** The Project on screen, or null on Global Sol (M11 / D-041). Context only. */
  readonly currentProjectId: string | null;
  /** Its name, for the context line. **Never its id** — see `sol-wording.ts`. */
  readonly currentProjectName: string | null;
  /**
   * An earlier conversation to REOPEN (M11 / D-041), or null for the ordinary
   * entry. The visit is pointed at it and its stored messages are drawn back;
   * the next question continues it. **An identifier, not a permission.**
   */
  readonly openConversationId?: string | null | undefined;
}

export function SolConversation({
  currentProjectId,
  currentProjectName,
  openConversationId,
}: SolConversationProps) {
  const { profile } = useSession();
  const timeZone = profile?.timezone ?? null;

  // **ONE PLAYBACK FOR THE WHOLE SCREEN.** Held here rather than inside an
  // answer so that starting one answer stops another, and so that asking a new
  // question stops whatever is talking — both structurally, not by convention.
  const readAloud = useSolReadAloud();

  // The composer's text lives in the hook, because its lifecycle IS the ask
  // lifecycle: cleared when a request starts, handed back when one could not be
  // made at all.
  const {
    exchanges,
    restored,
    restoring,
    pendingQuestion,
    problem,
    draft,
    setDraft,
    ask,
    retryLast,
    startOver,
  } = useSolAsk(currentProjectId, openConversationId ?? null);

  /**
   * VOICE TYPING — and **ONLY ONE AUDIO DIRECTION AT A TIME.**
   *
   * A phone recording while Crewlink is talking records Crewlink talking, so
   * the microphone stops Read Aloud before it opens. The reverse is handled by
   * disabling Read Aloud while the microphone is busy.
   *
   * **NOTHING IS SUBMITTED AUTOMATICALLY.** The words land in the composer and
   * a person decides. This is voice typing, not the future global microphone.
   */
  const dictation = useDictation({
    onBeforeStart: () => {
      readAloud.stop();
    },
    onTranscript: (transcript) => {
      // The existing Crewlink convention: an empty field becomes the
      // transcript; typed words are kept and the transcript follows on its own
      // line. **Nothing he can see on screen is overwritten.**
      setDraft(deliverTranscript(draft, transcript));
    },
  });

  const busy = pendingQuestion !== null;
  // **DISABLED FOR A DUPLICATE, NOT FOR BEING BUSY.** A different question is
  // allowed to supersede one still in flight — he is not made to wait out an
  // answer he no longer wants. Re-submitting the SAME words that are already in
  // flight is the double tap, and it is refused here and in the hook.
  // **BLOCKED WHILE THE WORDS ARE STILL ARRIVING.** Submitting mid-transcription
  // would ask Sol a question he has not finished dictating.
  const canSubmit =
    draft.trim().length > 0 && draft.trim() !== pendingQuestion && !dictation.isBusy;

  const current = exchanges[exchanges.length - 1];
  const earlier = exchanges.slice(0, -1);

  // **STORED MESSAGES, AS QUESTION + ANSWER PAIRS** (M11 / D-041). A user
  // message followed by a Sol message is one earlier exchange; an unpaired
  // message is shown on its own. Nothing is re-interpreted: the Sol text is the
  // stored sentence, drawn back as itself, with no cards and no proposal.
  const restoredPairs: { key: string; question: string | null; answer: string | null }[] = [];
  for (let index = 0; index < restored.length; index += 1) {
    const message = restored[index]!;
    const next = restored[index + 1];
    if (message.role === 'user' && next !== undefined && next.role === 'sol') {
      restoredPairs.push({ key: `r-${message.seq}`, question: message.body, answer: next.body });
      index += 1;
      continue;
    }
    restoredPairs.push({
      key: `r-${message.seq}`,
      question: message.role === 'user' ? message.body : null,
      answer: message.role === 'sol' ? message.body : null,
    });
  }
  const hasHistory = exchanges.length > 0 || restoredPairs.length > 0;

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    // **ASKING SOMETHING NEW STOPS THE VOICE.** Leaving an old answer talking
    // over a new question is how a listener attributes one to the other. Stop
    // first, so nothing survives even if the submit is then refused.
    readAloud.stop();
    // The visible half is `canSubmit`; this is the half that also holds for
    // Enter and for paste-then-submit.
    if (dictation.isBusy) return;
    // **THE HOOK IS THE AUTHORITY, NOT THE DISABLED ATTRIBUTE.**
    ask();
  }

  return (
    <section className="sol" aria-labelledby="sol-heading">
      <header className="sol-header">
        <h1 id="sol-heading" className="cl-screen-title">
          Sol
        </h1>
        {currentProjectName ? (
          <p className="sol-context">Asking about {currentProjectName}</p>
        ) : null}
      </header>

      {/* Shown ONCE on the surface. Repeating it under every answer is how a
          warning turns into wallpaper. */}
      <p className="sol-standing-warning">{SOL_STANDING_WARNING}</p>

      {/* **HISTORY AND MEMORY ARE ONE TAP AWAY** (M11 / D-040, D-041). The
          conversations are kept now, so the screen says where they are. */}
      <p className="sol-links">
        <Link className="cl-link" to={solHistoryPath(currentProjectId)}>
          Conversations
        </Link>
        <Link className="cl-link" to={SOL_MEMORY_PATH}>
          Memory
        </Link>
      </p>

      {/* **THE VISIBLE END OF A VISIT.** Start over begins a NEW conversation
          and moves the active pointer (M11 / D-041). **It deletes nothing**:
          the conversation it leaves is kept in Conversations, with every
          message, and can be reopened from there. */}
      {hasHistory ? (
        <p className="sol-visit-controls">
          <button
            type="button"
            className="sol-start-over"
            onClick={() => {
              readAloud.stop();
              startOver();
            }}
          >
            Start over
          </button>
        </p>
      ) : null}

      {!hasHistory && !busy && !restoring && problem === null ? (
        <p className="sol-empty">{SOL_EMPTY_PROMPT}</p>
      ) : null}

      {restoring && restoredPairs.length === 0 ? (
        <p className="sol-pending" role="status">
          Loading this conversation…
        </p>
      ) : null}

      {/* **THE STORED CONVERSATION, DRAWN BACK** (M11 / D-041). After a restart,
          or when an earlier conversation is reopened, what was said comes back
          from the server — exactly the words, as earlier questions. Cards and
          proposals are not re-issued from stored text. */}
      {restoredPairs.length > 0 ? (
        <section className="sol-restored" aria-labelledby="sol-restored-heading">
          <h2 id="sol-restored-heading" className="sol-restored-heading">
            Earlier in this conversation
          </h2>
          <p className="sol-restored-note">
            Answers here are what Sol said at the time. Ask again for what is true now.
          </p>
          {restoredPairs.map((pair) => (
            <article key={pair.key} className="sol-exchange sol-exchange--earlier">
              {pair.question !== null ? <p className="sol-question">{pair.question}</p> : null}
              {pair.answer !== null ? (
                <SolAnswerView
                  presentation={presentRestoredSolMessage(pair.answer)}
                  timeZone={timeZone}
                />
              ) : null}
            </article>
          ))}
        </section>
      ) : null}

      {earlier.length > 0 ? (
        <section className="sol-earlier" aria-labelledby="sol-earlier-heading">
          <h2 id="sol-earlier-heading" className="sol-earlier-heading">
            Earlier questions
          </h2>
          {earlier.map((exchange) => (
            <article key={exchange.id} className="sol-exchange sol-exchange--earlier">
              <p className="sol-question">{exchange.question}</p>
              <SolAnswerView presentation={exchange.presentation} timeZone={timeZone} />
              {exchange.memoryNoted !== null ? (
                <SolMemoryNotedCard memory={exchange.memoryNoted} reason="noted" />
              ) : null}
            </article>
          ))}
        </section>
      ) : null}

      {/* **ONE LIVE REGION, POLITE.** The pending line, the answer and the
          failure all land here, so a screen reader is told once that something
          arrived rather than being interrupted per element. */}
      <div className="sol-live" aria-live="polite">
        {busy ? (
          <article className="sol-exchange sol-exchange--pending">
            <p className="sol-question">{pendingQuestion}</p>
            <p className="sol-pending">Sol is reading your Captures…</p>
          </article>
        ) : null}

        {!busy && current ? (
          <article className="sol-exchange sol-exchange--current">
            <p className="sol-question">{current.question}</p>
            <SolAnswerView
              presentation={current.presentation}
              timeZone={timeZone}
              onRetry={retryLast}
              retryDisabled={busy}
              // **ONLY AN ELIGIBLE GROUNDED ANSWER THE SERVER AUTHORISED**
              // (D-04-5). No ticket means Read Aloud is off, unconfigured or the
              // content could not be prepared safely — and in each of those the
              // honest thing is no control, not one that apologises when tapped.
              // **A PROPOSAL, ON THE NEWEST ANSWER ONLY.** Earlier answers keep
              // no controls at all — and a proposal older than one question is
              // past its lifetime anyway, so offering it would be offering a
              // button that can only refuse.
              action={
                current.presentation.proposal === null ? undefined : isMyDayProposal(
                    current.presentation.proposal,
                  ) ? (
                  <SolMyDayProposalCard
                    key={current.id}
                    proposal={current.presentation.proposal}
                    disabled={dictation.isBusy}
                  />
                ) : (
                  <SolActionProposalCard
                    key={current.id}
                    proposal={current.presentation.proposal}
                    disabled={dictation.isBusy}
                  />
                )
              }
              readAloud={
                current.presentation.kind === 'grounded_answer' &&
                current.presentation.speech !== null ? (
                  <SolReadAloud
                    answerId={current.id}
                    speech={current.presentation.speech}
                    controller={readAloud}
                    disabled={dictation.isBusy}
                  />
                ) : undefined
              }
            />
            {/* **MEMORY IS VISIBLE, ALWAYS** (M11 / D-040). What Sol remembered
                on this turn — on its own, or because he asked — sits under the
                answer with Forget one tap away; "clear all" asks first. None of
                these is the operational Confirm card, and none reaches one. */}
            {current.memoryNoted !== null ? (
              <SolMemoryNotedCard
                key={`noted-${current.id}`}
                memory={current.memoryNoted}
                reason="noted"
                disabled={dictation.isBusy}
              />
            ) : null}
            {current.presentation.memory?.kind === 'saved' ? (
              <SolMemoryNotedCard
                key={`saved-${current.id}`}
                memory={current.presentation.memory.memory}
                reason="saved"
                disabled={dictation.isBusy}
              />
            ) : null}
            {current.presentation.memory?.kind === 'clear_requested' ? (
              <SolMemoryClearCard
                key={`clear-${current.id}`}
                activeCount={current.presentation.memory.activeCount}
                disabled={dictation.isBusy}
              />
            ) : null}
          </article>
        ) : null}

        {problem !== null ? (
          <p className="sol-problem" role="alert">
            {problem === 'signed_out' ? SIGNED_OUT_TEXT : ASK_UNAVAILABLE_TEXT}
          </p>
        ) : null}
      </div>

      <form className="sol-composer" onSubmit={handleSubmit}>
        <label className="sol-composer-label" htmlFor="sol-question">
          Ask Sol
        </label>
        <textarea
          id="sol-question"
          className="sol-composer-input"
          value={draft}
          rows={2}
          placeholder="Ask about anything you've captured"
          onChange={(event) => {
            setDraft(event.target.value);
          }}
        />
        <div className="sol-composer-actions">
          {/* **VOICE TYPING, NOT A COMMAND.** It fills the box; it never asks.
              Hidden entirely on a runtime with no recorder, because a control
              that cannot work is worse than no control. */}
          {dictation.isSupported ? (
            <button
              type="button"
              className="sol-dictate"
              aria-pressed={dictation.state === 'recording'}
              disabled={dictation.state === 'stopping' || dictation.state === 'transcribing'}
              onClick={() => {
                if (dictation.state === 'recording') {
                  dictation.stop();
                  return;
                }
                dictation.start();
              }}
            >
              {dictation.state === 'recording' ? 'Stop recording' : 'Dictate question'}
            </button>
          ) : null}

          {/* **DISABLED, NOT SILENTLY IGNORED.** The hook refuses an empty or
              duplicate submit as well — this is the visible half of the same
              rule, so Enter and paste-then-submit behave the same way. */}
          <button type="submit" className="cl-button sol-composer-submit" disabled={!canSubmit}>
            {busy ? 'Asking…' : 'Ask'}
          </button>
        </div>

        {/* Said as a sentence, never as a colour or an icon state. Typing keeps
            working through every one of these. */}
        {dictation.state === 'recording' ? (
          <p className="sol-dictate-note" role="status">
            Recording your question…
          </p>
        ) : null}
        {dictation.state === 'stopping' || dictation.state === 'transcribing' ? (
          <p className="sol-dictate-note" role="status">
            Turning your words into text…
          </p>
        ) : null}
        {dictation.error !== null ? (
          <p className="sol-dictate-note sol-dictate-note--failure" role="alert">
            {dictation.error} You can still type your question.
          </p>
        ) : null}
      </form>
    </section>
  );
}
